import { forwardRef, useEffect } from 'react';
import { buttonVariants } from './Button.style';
import { ButtonProps, ButtonType, ButtonSize, ButtonVariant } from './Button.types';
import { cn, inlineCenter } from '@/lib/styleUtils';
import { Colors } from '@/lib/colorUtils';
import { DotLoader, DotLoaderSize } from '../loaders/dot-loader/DotLoader';
import { logger } from '@/lib/logger';

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      buttonType = ButtonType.button,
      size = ButtonSize.medium,
      variant = ButtonVariant.primary,
      isLoading = false,
      leftIcon,
      rightIcon,
      className,
      disabled = false,
      ...rest
    },
    ref
  ) => {
    // Warn in development when icon-only buttons lack an accessible label
    useEffect(() => {
      if (process.env.NODE_ENV === 'development' && !children && !rest['aria-label']) {
        logger.warn('Button: Icon-only buttons require an aria-label prop for accessibility.', {
          feature: 'button',
          route: 'a11y-check',
        });
      }
    }, [children, rest]);

    const getLoaderColor = () => {
      if (
        buttonType === ButtonType.button &&
        (variant === ButtonVariant.primary || variant === ButtonVariant.danger)
      ) {
        return Colors.colorBrandWhite;
      }
      return undefined;
    };

    const getLoaderSize = () => {
      if (size === ButtonSize.large) {
        return DotLoaderSize.medium;
      }
      return DotLoaderSize.small;
    };

    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          buttonVariants({
            buttonType,
            size,
            variant,
            isLoading,
          }),
          className
        )}
        aria-busy={isLoading}
        disabled={disabled || isLoading}
        {...rest}
      >
        {isLoading ? (
          <DotLoader size={getLoaderSize()} color={getLoaderColor()} />
        ) : (
          <>
            {leftIcon && (
              <span className={inlineCenter} aria-hidden="true">
                {leftIcon}
              </span>
            )}
            {children}
            {rightIcon && (
              <span className={inlineCenter} aria-hidden="true">
                {rightIcon}
              </span>
            )}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
